/* eslint-disable security/detect-object-injection */
import React, {useState, useEffect, useCallback, useMemo} from 'react';
import {Helmet} from 'react-helmet';
import {useQueries, useQueryClient} from 'react-query';
import {useSearchParams, useNavigate} from 'react-router-dom';

import {ApplicationShell} from '../../../core/layout/application-shell';
import {useAxios} from 'src/utils/http';

import {
  StatusResponse,
  BucketInfo,
  BUCKET_ORDER,
  computeBuckets,
  findNextAction,
  prefixHasActionAvailable,
} from './bucket-model';
import {PrefixCard} from './prefix-card';
import {BucketModal} from './bucket-modal';
import {NewFromCSVModal} from './new-from-csv-modal';

const STORAGE_KEY = 'batch-administrator-prefixes-v1';
const REFETCH_MS = 30_000;

function loadTrackedPrefixes(): string[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(p => typeof p === 'string');
    return [];
  } catch {
    return [];
  }
}

function saveTrackedPrefixes(prefixes: string[]) {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefixes));
}

export const BatchAdministratorPage = () => {
  const api = useAxios();
  const qc = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [prefixes, setPrefixes] = useState<string[]>(() =>
    loadTrackedPrefixes()
  );
  const [newPrefixInput, setNewPrefixInput] = useState('');
  const [openBucket, setOpenBucket] = useState<{
    prefix: string;
    bucketIndex: number;
  } | null>(null);
  const [showNewCSV, setShowNewCSV] = useState(false);

  useEffect(() => saveTrackedPrefixes(prefixes), [prefixes]);

  // Parallel fetches across all tracked prefixes.
  const statusQueries = useQueries(
    prefixes.map(prefix => ({
      queryKey: ['batch-status', prefix],
      queryFn: () =>
        api
          .get('/v1/admin/datasets/batch-status', {params: {prefix}})
          .then(res => res.data as StatusResponse),
      refetchInterval: REFETCH_MS,
      retry: false,
    }))
  );

  const statusesByPrefix = useMemo(() => {
    const map: Record<string, StatusResponse | undefined> = {};
    prefixes.forEach((p, i) => {
      map[p] = statusQueries[i]?.data;
    });
    return map;
  }, [prefixes, statusQueries]);

  // Deep-link: read ?prefix=X&bucket=N when statuses arrive and auto-open.
  useEffect(() => {
    const pParam = searchParams.get('prefix');
    const bParam = searchParams.get('bucket');
    if (!pParam || !bParam) return;
    const status = statusesByPrefix[pParam];
    if (!status) return;
    const idx = parseInt(bParam, 10);
    if (Number.isNaN(idx) || idx < 0 || idx >= BUCKET_ORDER.length) return;
    const bucket = computeBuckets(status)[idx];
    if (
      bucket &&
      (!openBucket ||
        openBucket.prefix !== pParam ||
        openBucket.bucketIndex !== idx)
    ) {
      setOpenBucket({prefix: pParam, bucketIndex: idx});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, statusesByPrefix]);

  const addPrefix = useCallback(() => {
    const cleaned = newPrefixInput.trim();
    if (cleaned.length < 4 || prefixes.includes(cleaned)) return;
    setPrefixes([...prefixes, cleaned]);
    setNewPrefixInput('');
  }, [newPrefixInput, prefixes]);

  const removePrefix = useCallback(
    (p: string) => {
      setPrefixes(prefixes.filter(x => x !== p));
      if (openBucket?.prefix === p) setOpenBucket(null);
    },
    [prefixes, openBucket]
  );

  const openBucketFor = useCallback(
    (prefix: string, bucket: BucketInfo) => {
      setOpenBucket({prefix, bucketIndex: bucket.index});
      const next = new URLSearchParams(searchParams);
      next.set('prefix', prefix);
      next.set('bucket', String(bucket.index));
      setSearchParams(next, {replace: true});
    },
    [searchParams, setSearchParams]
  );

  const closeBucket = useCallback(() => {
    setOpenBucket(null);
    const next = new URLSearchParams(searchParams);
    next.delete('prefix');
    next.delete('bucket');
    setSearchParams(next, {replace: true});
  }, [searchParams, setSearchParams]);

  const onJumpToNext = useCallback(() => {
    const next = findNextAction(statusesByPrefix);
    if (!next) return;
    const status = statusesByPrefix[next.prefix];
    if (!status) return;
    const bucket = computeBuckets(status)[next.bucket];
    if (bucket) openBucketFor(next.prefix, bucket);
  }, [statusesByPrefix, openBucketFor]);

  const onCreated = useCallback(
    (newPrefix: string) => {
      if (!prefixes.includes(newPrefix)) {
        setPrefixes([...prefixes, newPrefix]);
      }
      setShowNewCSV(false);
      // Immediately poke the query so the new card fills in fast.
      setTimeout(() => {
        qc.invalidateQueries(['batch-status', newPrefix]);
      }, 500);
    },
    [prefixes, qc]
  );

  // Open new-from-csv via /admin/datasets/batch/new URL.
  useEffect(() => {
    if (window.location.pathname.endsWith('/batch/new')) {
      setShowNewCSV(true);
    }
  }, []);

  // Summary strip numbers.
  const {totalDatasets, prefixesWithAction, nextActionTarget} = useMemo(() => {
    let totalDS = 0;
    let prefixWA = 0;
    prefixes.forEach(p => {
      const s = statusesByPrefix[p];
      if (!s) return;
      totalDS += s.total;
      if (prefixHasActionAvailable(s)) prefixWA += 1;
    });
    return {
      totalDatasets: totalDS,
      prefixesWithAction: prefixWA,
      nextActionTarget: findNextAction(statusesByPrefix),
    };
  }, [prefixes, statusesByPrefix]);

  return (
    <>
      <Helmet>
        <title>Segmed Openda - Batch Administrator</title>
      </Helmet>

      <ApplicationShell>
        <div className="max-w-6xl mx-auto">
          <Header onNewClick={() => setShowNewCSV(true)} />

          <AddPrefixInput
            value={newPrefixInput}
            onChange={setNewPrefixInput}
            onAdd={addPrefix}
            existing={prefixes}
          />

          <SummaryStrip
            prefixCount={prefixes.length}
            totalDatasets={totalDatasets}
            prefixesWithAction={prefixesWithAction}
            canJump={nextActionTarget !== null}
            onJumpToNext={onJumpToNext}
          />

          {prefixes.length === 0 && (
            <EmptyState onNewClick={() => setShowNewCSV(true)} />
          )}

          {prefixes.map((prefix, idx) => {
            const q = statusQueries[idx];
            return (
              <PrefixCard
                key={prefix}
                prefix={prefix}
                status={q?.data}
                isLoading={q?.isLoading ?? false}
                isError={q?.isError ?? false}
                onBucketClick={b => openBucketFor(prefix, b)}
                onRefresh={() => q?.refetch()}
                onRemove={() => removePrefix(prefix)}
              />
            );
          })}

          {openBucket &&
            (() => {
              // Recompute the bucket from the live status on every render so
              // counts/eligibility stay fresh after a batch action invalidates
              // the status query. Otherwise the modal would keep showing the
              // snapshot taken when the pill was clicked.
              const liveStatus = statusesByPrefix[openBucket.prefix];
              if (!liveStatus) return null;
              const liveBucket =
                computeBuckets(liveStatus)[openBucket.bucketIndex];
              if (!liveBucket) return null;
              return (
                <BucketModal
                  prefix={openBucket.prefix}
                  bucket={liveBucket}
                  status={liveStatus}
                  onClose={closeBucket}
                />
              );
            })()}

          {showNewCSV && (
            <NewFromCSVModal
              onClose={() => {
                setShowNewCSV(false);
                if (window.location.pathname.endsWith('/batch/new')) {
                  navigate('/admin/datasets/batch', {replace: true});
                }
              }}
              onCreated={onCreated}
            />
          )}
        </div>
      </ApplicationShell>
    </>
  );
};

const Header = ({onNewClick}: {onNewClick: () => void}) => (
  <div className="flex items-center justify-between mb-4">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">Batch Administrator</h1>
      <p className="text-sm text-gray-500">
        Track and advance dataset delivery waves. Click a bucket to see its
        datasets and perform the next action.
      </p>
    </div>
    <button
      className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
      onClick={onNewClick}
    >
      + New Batch from CSV
    </button>
  </div>
);

const AddPrefixInput = ({
  value,
  onChange,
  onAdd,
  existing,
}: {
  value: string;
  onChange: (v: string) => void;
  onAdd: () => void;
  existing: string[];
}) => {
  const cleaned = value.trim();
  const disabled = cleaned.length < 4 || existing.includes(cleaned);
  return (
    <div className="mb-4 p-4 bg-white border border-gray-200 rounded-lg flex items-end gap-3">
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Track existing prefix
        </label>
        <input
          type="text"
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          placeholder="e.g. pip-2540 or dataset_delivery_dev"
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !disabled) onAdd();
          }}
        />
      </div>
      <button
        className="px-4 py-2 text-sm rounded-md bg-gray-800 text-white hover:bg-gray-900 disabled:opacity-40"
        disabled={disabled}
        onClick={onAdd}
      >
        Track
      </button>
    </div>
  );
};

const SummaryStrip = ({
  prefixCount,
  totalDatasets,
  prefixesWithAction,
  canJump,
  onJumpToNext,
}: {
  prefixCount: number;
  totalDatasets: number;
  prefixesWithAction: number;
  canJump: boolean;
  onJumpToNext: () => void;
}) => {
  if (prefixCount === 0) return null;
  return (
    <div className="mb-4 p-3 bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg flex items-center justify-between">
      <div className="text-sm text-gray-700">
        <b>{prefixCount}</b> prefix{prefixCount === 1 ? '' : 'es'} tracked ·{' '}
        <b>{totalDatasets.toLocaleString()}</b> datasets total ·{' '}
        {prefixesWithAction > 0 ? (
          <span className="text-green-700 font-medium">
            ⚡ {prefixesWithAction} with action available
          </span>
        ) : (
          <span className="text-gray-500">no actions pending</span>
        )}
      </div>
      <button
        className={`px-3 py-1.5 text-sm rounded-md ${
          canJump
            ? 'bg-green-600 text-white hover:bg-green-700'
            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
        }`}
        onClick={onJumpToNext}
        disabled={!canJump}
      >
        Jump to next action →
      </button>
    </div>
  );
};

const EmptyState = ({onNewClick}: {onNewClick: () => void}) => (
  <div className="p-12 bg-white border-2 border-dashed border-gray-200 rounded-lg text-center">
    <div className="text-gray-500 mb-4">
      No prefixes tracked yet. Start by creating a new batch from a CSV or
      tracking an existing prefix above.
    </div>
    <button
      className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
      onClick={onNewClick}
    >
      + New Batch from CSV
    </button>
  </div>
);
